import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { RemoveDeliveredVisualsResult, useRemoveDeliveredVisuals } from 'dataQueries/assignmentVisuals.query'
import { Stack, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'

import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import Modal from 'components/common/Modals/Modal/Modal'
import { QueryStatus } from 'components/common/QueryStatus'
import { isPurchasedVisual } from './removeDeliveredVisuals'
import { useTranslation } from 'react-i18next'

/** Longest reason the backend accepts */
export const MAX_REMOVAL_REASON_LENGTH = 1000

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Whether the modal is open */
  isOpen: boolean
  /** Assignment the visuals belong to */
  assignmentId: string
  /** Names of the selected delivered visuals */
  filenames: string[]
  /** Names of the visuals the client purchased */
  purchasedFilenames: ReadonlySet<string>
  /** Invoked when the modal closes without a removal */
  onClose: () => void
  /** Invoked with the outcome once the removal went through */
  onRemoved: (result: RemoveDeliveredVisualsResult) => void
}

/**
 * @component Lets an admin confirm the removal of delivered visuals and give the reason recorded in the audit log.
 */
export const RemoveDeliveredVisualsModal: React.FC<Props> = ({
  isOpen,
  assignmentId,
  filenames,
  purchasedFilenames,
  onClose,
  onRemoved,
}) => {
  const { t } = useTranslation(['gallery', 'actions'])
  const removeVisuals = useRemoveDeliveredVisuals()
  const [reason, setReason] = useState('')

  const sortedFilenames = useMemo(() => [...filenames].sort(), [filenames])
  const isConfirmDisabled = !reason.trim() || filenames.length === 0 || removeVisuals.isPending

  // A close while the request runs would drop its outcome, so it waits
  const requestClose = useCallback(() => {
    if (removeVisuals.isPending) return
    setReason('')
    removeVisuals.reset()
    onClose()
  }, [onClose, removeVisuals])

  const confirm = useCallback(() => {
    removeVisuals.mutate(
      { assignmentId, filenames: sortedFilenames, reason: reason.trim() },
      {
        onSuccess: ({ data }) => {
          setReason('')
          removeVisuals.reset()
          onRemoved(data)
        },
      }
    )
  }, [assignmentId, onRemoved, reason, removeVisuals, sortedFilenames])

  return (
    <Modal
      isOpen={isOpen}
      onClickOutside={requestClose}
      onClose={requestClose}
      title={t('remove_delivered_visuals_title')}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap=".8rem" paddingBottom=".8rem">
          <MUIButton type="secondaryNoBorder" onClick={requestClose}>
            {t('actions:Cancel')}
          </MUIButton>
          <MUIButton type="orangePrimary" disabled={isConfirmDisabled} onClick={confirm}>
            {t('remove_delivered_visuals_confirm')}
          </MUIButton>
        </Stack>
      }
    >
      <Stack gap="1.6rem" paddingBottom="2.4rem">
        <Typography variant="text-sm" color={GRAY_700}>
          {t('remove_delivered_visuals_description')}
        </Typography>

        <Stack component="ul" gap=".8rem" margin={0} padding={0} sx={{ listStyle: 'none' }}>
          {sortedFilenames.map(filename => (
            <Stack component="li" key={filename} direction="row" alignItems="center" justifyContent="space-between" gap="1.6rem">
              <Typography variant="text-sm" color={GRAY_900} sx={{ wordBreak: 'break-all' }}>{filename}</Typography>
              {isPurchasedVisual(filename, purchasedFilenames)
                ? <MUIBadge label={t('remove_delivered_visuals_purchased')} size="sm" color="green" />
                : <MUIBadge label={t('remove_delivered_visuals_not_purchased')} size="sm" color="gray" />
              }
            </Stack>
          ))}
        </Stack>

        <MUIInputField
          label={t('remove_delivered_visuals_reason')}
          value={reason}
          required
          isMultiline
          maxLength={MAX_REMOVAL_REASON_LENGTH}
          onChange={e => setReason(e.target.value.slice(0, MAX_REMOVAL_REASON_LENGTH))}
        />

        <QueryStatus query={removeVisuals} onPurge={() => removeVisuals.reset()} />
      </Stack>
    </Modal>
  )
}
